/******/ (() => { // webpackBootstrap
var __webpack_exports__ = {};
/*!********************************************!*\
  !*** ./src/assets/js/selling-plan-form.js ***!
  \********************************************/
function getSkioSellingPlanGroups(product) {
  if (!product?.selling_plan_groups?.length) return [];
  return product.selling_plan_groups.filter(group => group.app_id === 'SKIO');
}
function findSkioSellingPlanById(product, sellingPlanId) {
  const id = parseInt(sellingPlanId, 10);
  for (const group of getSkioSellingPlanGroups(product)) {
    const plan = (group.selling_plans || []).find(p => p.id === id);
    if (plan) return plan;
  }
  return null;
}
function getQuantityOption(product) {
  const options = product?.options;
  if (!Array.isArray(options)) return null;
  for (let i = 0; i < options.length; i++) {
    const option = options[i];
    const name = (typeof option === 'string' ? option : option?.name || '').toString().trim().toLowerCase();
    if (!name) continue;
    const isQuantity = name === 'quantity' || name === 'qty' || name === 'quanitty' || name.includes('bundle');
    if (!isQuantity) continue;
    const position = typeof option === 'object' && option?.position ? option.position : i + 1;
    return {
      position,
      values: getOrderedOptionValues(product, position, option)
    };
  }
  return null;
}
function getOrderedOptionValues(product, position, option) {
  if (option && Array.isArray(option.values) && option.values.length) {
    return option.values.map(value => typeof value === 'string' ? value : value?.name ?? value?.title ?? String(value));
  }
  const ordered = [];
  for (const variant of product?.variants || []) {
    const value = variant['option' + position] ?? variant.options?.[position - 1];
    if (value != null && !ordered.includes(value)) ordered.push(value);
  }
  return ordered;
}
function getQuantityIndexForVariant(product, variant) {
  const quantityOption = getQuantityOption(product);
  if (!quantityOption || !variant) return -1;
  const value = variant['option' + quantityOption.position] ?? variant.options?.[quantityOption.position - 1];
  return quantityOption.values.indexOf(value);
}
if (!customElements.get('selling-plan-form')) {
  customElements.define('selling-plan-form', class SellingPlanForm extends HTMLElement {
    constructor() {
      super();
      this.currentProduct = null;
      this.currentVariant = null;
      this.productHandle = this.dataset.productHandle;
      this.mutationObserver = null;
      this.init();
    }
    init() {
      this.loadProduct();
      this.bindPurchaseOptions();
      this.bindVariantChanges();
      this.observeFormChanges();
      this.patchProductFormToggleButton();
      requestAnimationFrame(() => this.updateSubmitButtonLabel());
    }
    async loadProduct() {
      if (!this.productHandle) {
        const productUrl = this.closest('product-info')?.dataset.url;
        if (productUrl) {
          this.productHandle = productUrl.split('/products/')[1]?.split('?')[0]?.split('#')[0];
        }
      }
      if (!this.productHandle) return;
      const requestId = Date.now();
      this._lastRequestId = requestId;
      try {
        const cache = window._ambrosiaProductCache || new Map();
        if (!cache.has(this.productHandle)) {
          cache.set(this.productHandle, fetch(`/products/${this.productHandle}.js`).then(r => r.json()));
        }
        const product = await cache.get(this.productHandle);
        if (this._lastRequestId !== requestId) return;
        this.currentProduct = product;
        const variantIdInput = this.getVariantIdInput();
        const urlVariantId = this.getUrlVariantId();
        const candidateId = parseInt(variantIdInput?.value || '', 10) || urlVariantId;
        if (candidateId) {
          this.currentVariant = product.variants.find(v => v.id === candidateId) || product.variants[0];
        } else {
          this.currentVariant = product.variants[0];
        }
        this.updateSellingPlans(product);
        this.updateSellingPlansForVariant(this.currentVariant);
        this.updatePrice();
        this.updateSellingPlanInput();
      } catch (error) {
        console.error('Error loading product:', error);
      }
    }
    bindVariantChanges() {
      if (typeof subscribe !== 'undefined' && typeof PUB_SUB_EVENTS !== 'undefined') {
        this.variantChangeUnsubscriber = subscribe(PUB_SUB_EVENTS.variantChange, this.handleVariantChange.bind(this));
      }
      const variantIdInput = this.getVariantIdInput();
      if (variantIdInput) {
        variantIdInput.addEventListener('change', () => {
          this.handleVariantIdChange();
        });
      }
    }
    getVariantIdInput() {
      const scope = this.closest('pack-product-form') || this.closest('product-info') || document;
      return scope.querySelector('input[name="id"]');
    }
    getUrlVariantId() {
      try {
        const v = new URLSearchParams(window.location.search).get('variant');
        const n = parseInt(v || '', 10);
        return Number.isFinite(n) ? n : null;
      } catch (_) {
        return null;
      }
    }
    handleVariantChange(event) {
      if (!this.currentProduct || !event.data?.variant) return;
      this.currentVariant = event.data.variant;
      this.updateSellingPlansForVariant(this.currentVariant);
      this.updatePrice();
      requestAnimationFrame(() => {
        this.updateSellingPlanInput();
        this.updateSubmitButtonLabel();
      });
    }
    handleVariantIdChange() {
      const variantIdInput = this.getVariantIdInput();
      if (!variantIdInput?.value || !this.currentProduct) return;
      const variantId = parseInt(variantIdInput.value, 10);
      this.currentVariant = this.currentProduct.variants.find(v => v.id === variantId);
      if (this.currentVariant) {
        this.updateSellingPlansForVariant(this.currentVariant);
        this.updatePrice();
        requestAnimationFrame(() => {
          this.updateSellingPlanInput();
          this.updateSubmitButtonLabel();
        });
      }
    }
    updateSellingPlansForVariant(variant) {
      const sellingPlanSelect = this.querySelector('select[name="selling_plan"]');
      const subscriptionSection = this.closest('fieldset.purchase-options') || this.querySelector('.purchase-options');
      const validPlanIds = new Set((variant?.selling_plan_allocations || []).map(a => String(a.selling_plan_id)));
      if (validPlanIds.size === 0) {
        if (subscriptionSection) subscriptionSection.style.display = 'none';
        const onetimeRadio = this.querySelector('[name="purchase_option"][value="onetime"]');
        if (onetimeRadio && !onetimeRadio.checked) {
          onetimeRadio.checked = true;
          this.updatePurchaseOptionSelection();
          this.updateSellingPlanInput();
        }
        return;
      }
      if (subscriptionSection) subscriptionSection.style.display = '';
      if (!sellingPlanSelect || !this.currentProduct) return;
      const currentValue = sellingPlanSelect.value;
      sellingPlanSelect.innerHTML = '';
      getSkioSellingPlanGroups(this.currentProduct).forEach(group => {
        (group.selling_plans || []).forEach(plan => {
          const planName = (plan.name || plan.description || '').trim();
          if (!planName.startsWith('Delivery ')) return;
          if (!validPlanIds.has(String(plan.id))) return;
          const option = document.createElement('option');
          option.value = plan.id;
          option.textContent = planName;
          if (plan.id.toString() === currentValue) option.selected = true;
          sellingPlanSelect.appendChild(option);
        });
      });
      if (sellingPlanSelect.options.length > 0 && !sellingPlanSelect.value) {
        sellingPlanSelect.selectedIndex = 0;
      }
      this.updateSellingPlanForQuantity(variant);
    }
    updateSellingPlanForQuantity(variant) {
      const sellingPlanSelect = this.querySelector('select[name="selling_plan"]');
      if (!sellingPlanSelect || !sellingPlanSelect.options.length || !this.currentProduct) return;
      const quantityIndex = getQuantityIndexForVariant(this.currentProduct, variant || this.currentVariant);
      if (quantityIndex < 0) return;
      const isInitial = this._lastQuantityIndex === undefined;
      const quantityChanged = quantityIndex !== this._lastQuantityIndex;
      this._lastQuantityIndex = quantityIndex;
      if (!isInitial && !quantityChanged) return;
      const targetIndex = Math.min(Math.max(quantityIndex, 0), sellingPlanSelect.options.length - 1);
      if (sellingPlanSelect.selectedIndex === targetIndex) return;
      sellingPlanSelect.selectedIndex = targetIndex;
      sellingPlanSelect.dispatchEvent(new Event('change', {
        bubbles: true
      }));
    }
    bindPurchaseOptions() {
      this.updatePurchaseOptionSelection();
      this.updateSellingPlanInput();
      this.updateSubmitButtonLabel();
      this.querySelectorAll('[name="purchase_option"]').forEach(radio => {
        radio.addEventListener('change', () => {
          this.updatePurchaseOptionSelection();
          this.updateSellingPlanInput();
          this.updateSubmitButtonLabel();
        });
      });
      const sellingPlanSelect = this.querySelector('select[name="selling_plan"]');
      if (sellingPlanSelect) {
        sellingPlanSelect.addEventListener('change', () => {
          this.updatePrice();
          this.updateSellingPlanInput();
          this.updateSubmitButtonLabel();
        });
      }
    }
    updateSellingPlanInput() {
      const productFormElement = this.closest('product-info')?.querySelector('product-form');
      const productForm = productFormElement?.querySelector('form');
      if (!productForm) return;
      const subscriptionSelected = this.querySelector('[name="purchase_option"][value="subscription"]')?.checked;
      const sellingPlanSelect = this.querySelector('select[name="selling_plan"]');
      let sellingPlanInput = productForm.querySelector('input[name="selling_plan"]');
      if (subscriptionSelected && sellingPlanSelect?.value) {
        if (!sellingPlanInput) {
          sellingPlanInput = document.createElement('input');
          sellingPlanInput.type = 'hidden';
          sellingPlanInput.name = 'selling_plan';
          productForm.appendChild(sellingPlanInput);
        }
        sellingPlanInput.value = sellingPlanSelect.value;
      } else {
        if (sellingPlanInput) {
          sellingPlanInput.remove();
        }
      }
    }
    updatePurchaseOptionSelection() {
      this.querySelectorAll('.purchase-option').forEach(option => {
        option.classList.remove('selected');
      });
      const activeRadio = this.querySelector('[name="purchase_option"]:checked');
      if (activeRadio) {
        const purchaseOption = activeRadio.closest('.purchase-option');
        if (purchaseOption) {
          purchaseOption.classList.add('selected');
        }
      }
    }
    updateSubmitButtonLabel() {
      const productFormElement = this.closest('product-info')?.querySelector('product-form');
      if (!productFormElement) return;
      const submitButton = productFormElement.querySelector('[type="submit"]');
      if (!submitButton) return;
      const submitButtonText = submitButton.querySelector('span');
      if (!submitButtonText) return;
      if (submitButton.classList.contains('loading') || submitButton.hasAttribute('aria-disabled')) {
        return;
      }
      const subscriptionSelected = this.querySelector('[name="purchase_option"][value="subscription"]')?.checked;
      if (subscriptionSelected) {
        submitButtonText.textContent = 'Add to cart';
      } else {
        if (window.variantStrings && window.variantStrings.addToCart) {
          submitButtonText.textContent = window.variantStrings.addToCart;
        } else {
          submitButtonText.textContent = 'Add to cart';
        }
      }
    }
    patchProductFormToggleButton() {
      const productFormElement = this.closest('product-info')?.querySelector('product-form');
      if (!productFormElement) {
        setTimeout(() => this.patchProductFormToggleButton(), 100);
        return;
      }
      if (productFormElement.toggleSubmitButton && !productFormElement._originalToggleSubmitButton) {
        productFormElement._originalToggleSubmitButton = productFormElement.toggleSubmitButton.bind(productFormElement);
        const self = this;
        productFormElement.toggleSubmitButton = function (disable = true, text) {
          const subscriptionSelected = self.querySelector('[name="purchase_option"][value="subscription"]')?.checked;
          productFormElement._originalToggleSubmitButton(disable, text);
          if (!disable && subscriptionSelected) {
            requestAnimationFrame(() => {
              const submitButton = productFormElement.querySelector('[type="submit"]');
              const submitButtonText = submitButton?.querySelector('span');
              if (submitButtonText && !submitButton.classList.contains('loading')) {
                submitButtonText.textContent = 'Add to cart';
              }
            });
          }
        };
      }
    }
    updateSellingPlans(product) {
      const sellingPlanSelect = this.querySelector('select[name="selling_plan"]');
      if (!sellingPlanSelect) return;
      const currentValue = sellingPlanSelect.value;
      sellingPlanSelect.innerHTML = '';
      getSkioSellingPlanGroups(product).forEach(group => {
        if (group.selling_plans && group.selling_plans.length > 0) {
          group.selling_plans.forEach(plan => {
            const planName = (plan.name || plan.description || '').trim();
            if (!planName.startsWith('Delivery ')) {
              return;
            }
            const option = document.createElement('option');
            option.value = plan.id;
            option.textContent = planName || `Plan ${plan.id}`;
            if (plan.id.toString() === currentValue) {
              option.selected = true;
            }
            sellingPlanSelect.appendChild(option);
          });
        }
      });
      if (sellingPlanSelect.options.length > 0 && !sellingPlanSelect.value) {
        sellingPlanSelect.selectedIndex = 0;
      }
      this.updatePrice();
      this.updateSellingPlanInput();
    }
    updatePrice() {
      if (!this.currentVariant) return;
      const basePrice = this.currentVariant.price || 0;
      const selectedPackInput = this.closest('pack-product-form')?.querySelector('input[name="pack"]:checked');
      const packQuantity = Math.max(1, parseInt(selectedPackInput?.dataset?.packQuantity || '1', 10) || 1);
      const otPrice = basePrice * packQuantity;
      let sbsPrice = basePrice * packQuantity;
      const sellingPlanSelect = this.querySelector('select[name="selling_plan"]');
      if (sellingPlanSelect && sellingPlanSelect.value && this.currentProduct) {
        const plan = findSkioSellingPlanById(this.currentProduct, sellingPlanSelect.value);
        if (plan?.price_adjustments?.length > 0) {
          const adjustment = plan.price_adjustments[0];
          if (adjustment.value_type === 'percentage') {
            sbsPrice = sbsPrice * (1 - adjustment.value / 100);
          } else if (adjustment.value_type === 'fixed_amount') {
            sbsPrice = sbsPrice - adjustment.value * packQuantity;
          }
        }
      }
      const otPriceElement = this.querySelector('[data-option="onetime"] [data-pack-price]');
      if (otPriceElement) {
        if (typeof Shopify !== 'undefined' && Shopify.formatMoney) {
          otPriceElement.textContent = Shopify.formatMoney(otPrice);
        } else {
          otPriceElement.textContent = `$${(otPrice / 100).toFixed(2)}`;
        }
      }
      const sbsPriceElement = this.querySelector('[data-option="subscription"] [data-pack-price]');
      if (sbsPriceElement) {
        if (typeof Shopify !== 'undefined' && Shopify.formatMoney) {
          sbsPriceElement.textContent = Shopify.formatMoney(sbsPrice);
        } else {
          sbsPriceElement.textContent = `$${(sbsPrice / 100).toFixed(2)}`;
        }
      }
      const originalPriceElement = this.querySelector('[data-option="subscription"] .original-price');
      const discountBadge = this.querySelector('[data-option="subscription"] .discount-badge');
      if (originalPriceElement && discountBadge) {
        if (typeof Shopify !== 'undefined' && Shopify.formatMoney) {
          originalPriceElement.textContent = Shopify.formatMoney(otPrice);
        } else {
          originalPriceElement.textContent = `$${(otPrice / 100).toFixed(2)}`;
        }
        const discountPercent = Math.round((otPrice - sbsPrice) / otPrice * 100);
        if (discountPercent > 0) {
          discountBadge.textContent = `Save ${discountPercent}%`;
          discountBadge.style.display = 'inline';
        } else {
          discountBadge.style.display = 'none';
        }
      }
    }
    observeFormChanges() {
      const productFormElement = this.closest('product-info')?.querySelector('product-form');
      const productForm = productFormElement?.querySelector('form');
      if (!productForm) return;
      this.mutationObserver = new MutationObserver(mutations => {
        const sellingPlanInput = productForm.querySelector('input[name="selling_plan"]');
        const subscriptionSelected = this.querySelector('[name="purchase_option"][value="subscription"]')?.checked;
        const sellingPlanSelect = this.querySelector('select[name="selling_plan"]');
        if (subscriptionSelected && sellingPlanSelect?.value && !sellingPlanInput) {
          this.updateSellingPlanInput();
        }
      });
      this.mutationObserver.observe(productForm, {
        childList: true,
        subtree: true
      });
      const submitButton = productFormElement.querySelector('[type="submit"]');
      if (submitButton) {
        const submitButtonText = submitButton.querySelector('span');
        if (submitButtonText) {
          let isUpdating = false;
          this.buttonObserver = new MutationObserver(() => {
            if (isUpdating) return;
            const subscriptionSelected = this.querySelector('[name="purchase_option"][value="subscription"]')?.checked;
            if (subscriptionSelected && submitButtonText.textContent !== 'Subscribe' && !submitButton.classList.contains('loading')) {
              isUpdating = true;
              requestAnimationFrame(() => {
                submitButtonText.textContent = 'Add to cart';
                isUpdating = false;
              });
            }
          });
          this.buttonObserver.observe(submitButtonText, {
            characterData: true,
            childList: true
          });
        }
      }
    }
    disconnectedCallback() {
      if (this.variantChangeUnsubscriber) {
        this.variantChangeUnsubscriber();
      }
      if (this.mutationObserver) {
        this.mutationObserver.disconnect();
      }
      if (this.buttonObserver) {
        this.buttonObserver.disconnect();
      }
    }
  });
}
/******/ })()
;